import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type CapturedLocation = {
  latitude: number;
  longitude: number;
  accuracy: number | null;
};

type Facing = 'environment' | 'user';

type Props = {
  onCapture: (imageDataUrl: string, location: CapturedLocation | null) => void;
  onReset: () => void;
};

const getCurrentLocation = (): Promise<CapturedLocation> =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('geolocation-not-supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) =>
        resolve({
          latitude: coords.latitude,
          longitude: coords.longitude,
          accuracy: Number.isFinite(coords.accuracy) ? coords.accuracy : null,
        }),
      reject,
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  });

const requestCamera = async (facing: Facing, deviceId?: string) => {
  if (deviceId) {
    try {
      return await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId } },
        audio: false,
      });
    } catch {
      // Si el deviceId ya no existe, caer al facingMode.
    }
  }
  try {
    return await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { exact: facing } },
      audio: false,
    });
  } catch {
    try {
      return await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: facing } },
        audio: false,
      });
    } catch {
      return navigator.mediaDevices.getUserMedia({ video: true, audio: false });
    }
  }
};

const listVideoInputs = async () => {
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices.filter((device) => device.kind === 'videoinput' && device.deviceId);
};

const waitForVideo = (video: HTMLVideoElement) =>
  new Promise<void>((resolve, reject) => {
    if (video.readyState >= 2 && video.videoWidth > 0) {
      resolve();
      return;
    }
    const onReady = () => {
      video.removeEventListener('loadeddata', onReady);
      video.removeEventListener('error', onError);
      resolve();
    };
    const onError = () => {
      video.removeEventListener('loadeddata', onReady);
      video.removeEventListener('error', onError);
      reject(new Error('video'));
    };
    video.addEventListener('loadeddata', onReady);
    video.addEventListener('error', onError);
  });

export const RoadDamageCamera = ({ onCapture, onReset }: Props) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const startIdRef = useRef(0);
  const [capturedImage, setCapturedImage] = useState('');
  const [cameraError, setCameraError] = useState('');
  const [locationError, setLocationError] = useState('');
  const [locating, setLocating] = useState(false);
  const [ready, setReady] = useState(false);
  const [facing, setFacing] = useState<Facing>('environment');
  const [deviceId, setDeviceId] = useState<string | undefined>(undefined);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const startCamera = useCallback(async () => {
    const startId = ++startIdRef.current;
    stopCamera();
    setCameraError('');
    setReady(false);
    try {
      const stream = await requestCamera(facing, deviceId);
      if (startId !== startIdRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) return;
      video.srcObject = stream;
      await video.play();
      await waitForVideo(video);
      if (startId !== startIdRef.current) return;
      const inputs = await listVideoInputs();
      if (startId !== startIdRef.current) return;
      setCameras(inputs);
      setReady(true);
    } catch {
      if (startId === startIdRef.current) {
        setCameraError(t('roadDamages.cameraError'));
      }
    }
  }, [deviceId, facing, stopCamera, t]);

  useEffect(() => {
    if (capturedImage) return undefined;
    startCamera();
    return () => {
      startIdRef.current += 1;
      stopCamera();
    };
  }, [capturedImage, startCamera, stopCamera]);

  const switchCamera = () => {
    if (cameras.length > 1) {
      const currentId =
        streamRef.current?.getVideoTracks()[0]?.getSettings().deviceId || deviceId;
      const index = Math.max(0, cameras.findIndex((camera) => camera.deviceId === currentId));
      const next = cameras[(index + 1) % cameras.length];
      const label = `${next.label} ${next.deviceId}`.toLowerCase();
      setFacing(label.includes('front') || label.includes('user') || label.includes('delantera')
        ? 'user'
        : label.includes('back') || label.includes('rear') || label.includes('environment') || label.includes('trasera')
          ? 'environment'
          : facing === 'environment' ? 'user' : 'environment');
      setDeviceId(next.deviceId);
      return;
    }
    setDeviceId(undefined);
    setFacing((current) => (current === 'environment' ? 'user' : 'environment'));
  };

  const capture = async () => {
    const video = videoRef.current;
    if (!video?.videoWidth || !video.videoHeight) return;

    const maxWidth = 1280;
    const scale = Math.min(1, maxWidth / video.videoWidth);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    const context = canvas.getContext('2d');
    if (!context) return;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageDataUrl = canvas.toDataURL('image/jpeg', 0.78);

    setCapturedImage(imageDataUrl);
    startIdRef.current += 1;
    stopCamera();
    setLocating(true);
    setLocationError('');
    try {
      onCapture(imageDataUrl, await getCurrentLocation());
    } catch {
      setLocationError(t('roadDamages.locationError'));
      onCapture(imageDataUrl, null);
    } finally {
      setLocating(false);
    }
  };

  const retake = () => {
    setCapturedImage('');
    setLocationError('');
    onReset();
  };

  return (
    <div className="road-camera">
      {!capturedImage ? (
        <>
          <video ref={videoRef} autoPlay muted playsInline />
          {cameraError && <p className="road-error">{cameraError}</p>}
          {!cameraError && !ready && (
            <p className="road-note">{t('roadDamages.startingCamera')}</p>
          )}
          <div className="road-camera-actions">
            {cameras.length > 1 && (
              <button
                type="button"
                className="road-cancel"
                disabled={!ready}
                onClick={switchCamera}
              >
                {facing === 'environment'
                  ? t('roadDamages.switchToFrontCamera')
                  : t('roadDamages.switchToRearCamera')}
              </button>
            )}
            <button
              type="button"
              className="attach-damage-btn"
              disabled={!ready}
              onClick={capture}
            >
              {t('roadDamages.takePhoto')}
            </button>
          </div>
        </>
      ) : (
        <>
          <img src={capturedImage} alt={t('roadDamages.capturedPhoto')} />
          {locating && <p className="road-note">{t('roadDamages.gettingLocation')}</p>}
          {locationError && <p className="road-error">{locationError}</p>}
          <button type="button" className="road-cancel" disabled={locating} onClick={retake}>
            {t('roadDamages.retakePhoto')}
          </button>
        </>
      )}
    </div>
  );
};
